'use client';

import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { ensureFocusVisibleStyle } from '@galapagos/design-system/web';
import { PageColumn } from '@/components/page-column/PageColumn';
import { SignupHeader } from './SignupHeader';
import { SignupFooter } from './SignupFooter';
import { ensureSignupChromeStyle } from './signup-chrome-style';
import { trackRegFlowViewed } from '../services/regflow-analytics';

const FOCUS_STYLE_ID = 'signup-chrome-focus-visible';
// The step heading is listed so a keyboard user sees where focus lands when
// the shell moves it there on every step. Browsers treat focus moved by script
// on a fresh page load as keyboard focus, so the heading's ring is scoped to a
// member who has actually used the keyboard; the chrome stylesheet hides it
// otherwise.
const FOCUS_SELECTORS = [
  '.signup-language-switch__option',
  '.signup-footer__link',
  ".signup-chrome[data-signup-input='keyboard'] .signup-page-title__heading",
];

const INPUT_ATTRIBUTE = 'data-signup-input';
const KEYBOARD_NAVIGATION_KEYS = new Set([
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
]);

// Each step mounts its own shell, so the input a member last used is kept for
// the document rather than per shell: a keyboard Continue still shows the ring
// on the next step's heading.
let keyboardInput = false;
let listening = false;

function markInput(keyboard: boolean): void {
  keyboardInput = keyboard;
  for (const chrome of document.querySelectorAll('.signup-chrome')) {
    if (keyboard) chrome.setAttribute(INPUT_ATTRIBUTE, 'keyboard');
    else chrome.removeAttribute(INPUT_ATTRIBUTE);
  }
}

function listenForInput(): void {
  if (listening) return;
  listening = true;
  document.addEventListener(
    'keydown',
    (event) => {
      if (KEYBOARD_NAVIGATION_KEYS.has(event.key)) markInput(true);
    },
    true,
  );
  document.addEventListener('pointerdown', () => markInput(false), true);
}

/**
 * The page frame every signup step renders in: v8 header, one centred content
 * column, v8 footer. There is deliberately no back control anywhere in it.
 */
export function SignupShell({
  children,
  width = 'default',
  languageSwitch = true,
}: {
  children: ReactNode;
  /** `reading` widens the content column for long-form pages such as the legal documents. */
  width?: 'default' | 'reading';
  /** `false` leaves the EN | ES switch out of the header, for pages that exist in English only. */
  languageSwitch?: boolean;
}) {
  ensureSignupChromeStyle();
  ensureFocusVisibleStyle(FOCUS_STYLE_ID, FOCUS_SELECTORS);
  // The first frame a tab shows is the flow entry it landed on. The browser
  // path is read directly, which keeps the frame free of router hooks.
  const chromeRef = useRef<HTMLDivElement>(null);
  // Before the step heading takes focus, so its first paint already knows
  // which input the member last used.
  useLayoutEffect(() => {
    listenForInput();
    if (keyboardInput) chromeRef.current?.setAttribute(INPUT_ATTRIBUTE, 'keyboard');
  }, []);
  useEffect(() => {
    trackRegFlowViewed(window.location.pathname);
  }, []);
  return (
    <div ref={chromeRef} className="signup-chrome" data-testid="signup-chrome">
      <SignupHeader languageSwitch={languageSwitch} />
      <main className="signup-chrome__main">
        <PageColumn grow paddingTop="lg" width={width}>
          {children}
        </PageColumn>
      </main>
      <SignupFooter />
    </div>
  );
}
